import isNull from 'lodash/isNull'
import mapValues from 'lodash/mapValues'
import identity from 'lodash/identity'
import isPlainObject from 'lodash/isPlainObject'
import isBoolean from 'lodash/isBoolean'
import lowerCase from 'lodash/lowerCase'
import upperFirst from 'lodash/upperFirst'
import upperCase from 'lodash/upperCase'
import isFinite from 'lodash/isFinite'
import {
  LONDON,
  addCalendarDays,
  atLondonTime,
  dayjsLondon,
  dayjsUtc,
  londonToday,
} from 'lib/dayjs'

import { customAlphabet } from 'nanoid'
import sortBy from 'lodash/sortBy'

import { BrandId, FuneralBrandId } from 'config/brands'
import ENV from 'config/environment'
import { Address } from 'lib/models/address'
import { Constants } from 'lib/models/constants'
import { Contact } from 'lib/models/contact'
import { Event } from 'lib/models/event'
import { LeadAttributes } from 'lib/models/lead'
import { CommentAttributes } from 'lib/models/comment'
import { Invoice, InvoiceLineItem } from 'lib/models/invoice'
import { AdminUser } from 'lib/models/admin-user'
import { Celebrant } from 'lib/models/celebrant'
import { Country } from 'lib/models/country'
import { AdminUserSelectOptions } from 'routes/case-list/types'
import { getDefinedTimeslot } from 'routes/lead/helpers'

import {
  CELEBRATION_NONE,
  CIVIL_PARTNERSHIP,
  COLLECT,
  DELIVER,
  FAREWILL_PRODUCTS_OPTIONS,
  FUNERAL_LEAD_STATUS,
  INVOICE_LINE_ITEMS,
  LEAD_PARTNER_TYPES,
  LEAD_STATUS,
  MARRIED,
  MARRIED_OR_CIVIL_PARTNERSHIP,
  PRODUCTS,
  PRODUCTS_FOR_BRAND,
  SCATTER,
} from './enums'
import { Task } from 'lib/models/task'

type DateInput = Date | string | number | null | undefined

const FORMATS = {
  H: { long: ' hours ', short: 'h ' },
  M: { long: ' minutes ', short: 'm ' },
  S: { long: ' seconds', short: 's' },
}
export const formatSeconds = (
  d: number,
  format: 'short' | 'long' = 'short'
): string | '' => {
  d = Number(d)
  const h = Math.floor(d / 3600)
  const m = Math.floor((d % 3600) / 60)
  const s = Math.floor((d % 3600) % 60)

  const hDisplay = h > 0 ? h + FORMATS.H[format] : ''
  const mDisplay = m > 0 ? m + FORMATS.M[format] : ''
  const sDisplay = s > 0 ? s + FORMATS.S[format] : ''
  return hDisplay + mDisplay + sDisplay
}

const exactStringToBooleanMapping = {
  true: true,
  false: false,
}

export type strToBoolProp = 'true' | 'false' | undefined | null | boolean | ''

export function isStrBoolProp(flag: strToBoolProp): flag is strToBoolProp {
  return flag === 'true' || flag === 'false'
}

export function strToBool(str: strToBoolProp): boolean | undefined {
  if (isBoolean(str)) return str
  if (!str) return
  return exactStringToBooleanMapping[str]
}

export function boolToStr(
  bool: boolean | undefined | null
): 'true' | 'false' | undefined | null {
  if (bool === null || bool === undefined) return bool
  const boolAsString = bool.toString() as 'true' | 'false'
  return boolAsString
}

/** Ensures that a passed input returns as a number or null */
export const ensureNumberOrNull = (
  input: string | number | undefined
): number | null => {
  // return straight away if it is already a number
  if (typeof input === 'number') return input

  const value = typeof input === 'string' ? removeCommas(input) : input
  const stringAsNumber = parseFloat(value as string)
  if (isFinite(stringAsNumber)) return stringAsNumber
  return null
}

export const strToStrOrNull = (str: string): string | null => {
  if (str === '') return null

  return str
}

const isDateTime = (key: string): boolean =>
  ['factFindCallAt', 'scheduledNextCallAt'].includes(key)

//We want to add types to formatValuesForFormik but we're going to do that in a separate PR.

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any */
export function formatValuesForFormik(values: any): any {
  return mapValues(values, (value, key) => {
    if (isPlainObject(value)) return formatValuesForFormik(value)
    if (isNull(value)) return ''
    if (isBoolean(value)) return boolToStr(value)

    if (isDateTime(key)) {
      return value ? dayjsUtc(value).tz(LONDON).format('YYYY-MM-DDTHH:mm') : ''
    }

    return value
  })
}

export function findEvent(events: Event[], name: string): Event | undefined {
  return events.find((event) => {
    if (event.attributes) {
      return event.attributes.name === name
    } else {
      return event.name === name
    }
  })
}

export const findTask = (tasks: Task[], coreTask: string): Task | undefined => {
  return tasks.find((task) => task.attributes.coreTask === coreTask)
}

export function getFullName(contact?: Contact): string | null {
  if (!contact) return null
  const { firstName, lastName } = contact
  return [firstName, lastName].filter(identity).join(' ')
}

export const getFullNameWithPreferredName = (
  contact?: Contact
): null | string => {
  if (!contact) return null
  const { firstName, lastName, preferredName } = contact

  if (!firstName && !lastName && !!preferredName) return preferredName

  const formattedPreferredName = preferredName ? ` (${preferredName})` : ''

  return `${getFullName(contact)}${formattedPreferredName}`
}

type AircallContact = {
  first_name?: string
  last_name?: string
}

export const getAircallContactName = (
  contact?: AircallContact
): null | string => {
  if (!contact || !contact.first_name || !contact.last_name) return null
  return [contact.first_name, contact.last_name].filter(identity).join(' ')
}

/** Yesterday's date in London, as YYYY-MM-DD */
export const yesterday = (): string => addCalendarDays(londonToday(), -1)

export const dateIsYesterday = (date: string): boolean => {
  return dayjsLondon(date).format('YYYY-MM-DD') === yesterday()
}

export const dateIsToday = ({
  date,
  treatAnyTimeInPastAsOverdue = false,
}: {
  date: string
  treatAnyTimeInPastAsOverdue?: boolean
}): boolean => {
  const isToday = dayjsLondon(date).format('YYYY-MM-DD') === londonToday()
  return treatAnyTimeInPastAsOverdue
    ? isToday && dayjsUtc().isBefore(dayjsLondon(date))
    : isToday
}

/** Tomorrow's date in London, as YYYY-MM-DD */
export const tomorrow = (): string => addCalendarDays(londonToday(), 1)

export const dateIsTomorrow = (date: string): boolean => {
  return dayjsLondon(date).format('YYYY-MM-DD') === tomorrow()
}

export const formatDateTimeToUTC = (dateTime?: Date): null | string => {
  if (!dateTime) return null
  return dayjsUtc(dateTime).format('YYYY-MM-DDTHH:mm:ss[Z]')
}

export const formatDateTimeToString = (
  dateTime: DateInput,
  { includeTime = true } = {}
): null | string => {
  if (!dateTime) return null
  return dayjsUtc(dateTime)
    .tz(LONDON)
    .format(includeTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')
}

// Treats a datetime as a London date, not a UTC one
// This works when we want to store timezone-less dates from the
// API e.g. 2021-09-18
export const formatLocalDateTimeToString = (
  dateTime: DateInput,
  { includeTime = true, timeOnly = false } = {}
): null | string => {
  if (!dateTime) return null
  const date = dayjsLondon(dateTime)

  if (timeOnly) {
    return date.format('HH:mm')
  }

  return date.format(includeTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')
}

// The DatetimePicker component we use requires a JS date object
// so we construct one using the dateString, or today's date
// Since we don't store any timezone info, it doesn't matter what
// we use as when the time is saved, we extract a timezone-less time string
// from the date object
export const formatTimeToDateTimeString = (timeString: string): string => {
  if (!timeString) {
    return ''
  }

  const formattedDate = londonToday()

  return `${formattedDate}T${timeString}`
}

export interface Response {
  id: number
  type: string
  attributes: LeadAttributes | CommentAttributes
}
type FlattenedResponse = (LeadAttributes | CommentAttributes) & {
  id: number
  type: string
}

export const flattenAttributes = (response: Response): FlattenedResponse => {
  return { ...response, ...response.attributes }
}

export const formatToHuman = (value: string): string => {
  return upperFirst(lowerCase(value))
}

export const formatDateToISO = (
  year: number,
  month: number,
  day: number
): string | null => {
  if (!year || !month || !day) return null

  return `${year}-${month}-${day}`
}

export const removeCommas = (string?: string): string | undefined =>
  string?.replace(/,/g, '')

export const findLineItemByDescription = ({
  description,
  invoice,
}: {
  description: string
  invoice?: Invoice
}): InvoiceLineItem | undefined => {
  return invoice?.attributes.lineItems.find(
    (lineItem) => lineItem.description === description
  )
}

export const findDepositInvoice = (
  invoices: Invoice[]
): Invoice | undefined => {
  return invoices.find((invoice) =>
    findLineItemByDescription({
      invoice,
      description: INVOICE_LINE_ITEMS.DEPOSIT,
    })
  )
}

export const findCaseBookedInvoice = (
  invoices: Invoice[]
): Invoice | undefined => {
  return invoices.find((invoice) =>
    findLineItemByDescription({
      invoice,
      description: INVOICE_LINE_ITEMS.SERVICE_FEE,
    })
  )
}

export const generatePaymentReference = (): string => {
  return customAlphabet('2456789BCDFGHJKLMNPQRSTVWXYZ', 14)()
}

export const showPartnerSelect = (partnerType: string | null): boolean =>
  partnerType === LEAD_PARTNER_TYPES.charity ||
  partnerType === LEAD_PARTNER_TYPES.other_partner

export const showUnsuitableReasonSelect = (status: string): boolean =>
  status === LEAD_STATUS.LOST

export const showFuneralBlockedReasonSelect = (status: string): boolean =>
  status === FUNERAL_LEAD_STATUS.BLOCKED

const formatDateString = ({
  date,
  treatAnyTimeInPastAsOverdue = false,
}: {
  date: string
  treatAnyTimeInPastAsOverdue?: boolean
}): string => {
  if (dateIsToday({ date, treatAnyTimeInPastAsOverdue })) return 'today'
  if (dateIsYesterday(date)) return 'yesterday'
  if (dateIsTomorrow(date)) return 'tomorrow'
  const instant = dayjsUtc(dayjsLondon(date).toDate())
  if (instant.isAfter(dayjsUtc())) return dayjsLondon(date).format('D MMM')
  return treatAnyTimeInPastAsOverdue
    ? instant.from(dayjsUtc())
    : instant.from(dayjsUtc(atLondonTime(londonToday(), { hour: 0 }).toDate()))
}

export const nextTaskDueOnString = (
  date: string | undefined
): string | null => {
  if (!date) return null
  return formatDateString({ date })
}

export const getTimeDescriptionFromTimestamp = (
  date: string | undefined,
  showTime: boolean
) => {
  if (!date) return null
  let timeString = ''
  timeString += formatDateString({
    date,
    treatAnyTimeInPastAsOverdue: true,
  })

  const isCallTimeInPast = ['yesterday', 'ago'].some((word) =>
    timeString.includes(word)
  )

  if (showTime && !isCallTimeInPast) {
    const callTimeslot = getDefinedTimeslot(date)?.label

    timeString += callTimeslot
      ? ` ${callTimeslot}`
      : ` ${dayjsLondon(date).format('h:mmA')}`
  }

  return timeString
}

export const truncateText = (text: string, characterLimit: number): string => {
  if (text.length <= characterLimit) return text
  return `${text.substring(0, characterLimit)}...`
}

export const formatAdminUserOptions = (
  adminUsers: AdminUser[] = [],
  loggedInAdminUserId?: number
): AdminUserSelectOptions => {
  const orderedAdminUsers = [...adminUsers]

  if (loggedInAdminUserId) {
    orderedAdminUsers.sort((a, b) => {
      if (a.id === loggedInAdminUserId) return -1
      if (b.id === loggedInAdminUserId) return 1
      return 0
    })
  }

  return orderedAdminUsers.map((adminUser) => ({
    label: adminUser.attributes.name,
    value: adminUser.id,
  }))
}

interface CelebrantOption {
  label: string
  value: number
  isDeleted: boolean
}

export const formatCelebrantOptions = (
  celebrants: Celebrant[]
): CelebrantOption[] => {
  const allCelebrantOptions = celebrants.map((celebrant) => ({
    label: celebrant.attributes.name,
    value: celebrant.id,
    isDeleted: celebrant.attributes.isDeleted,
  }))

  const sortedNoCelebrantOptions: CelebrantOption[] = []

  const noCelebrantOptions = [
    'Not answered',
    'No celebrant',
    'Using a different celebrant',
    'Celebrant to be decided',
  ]

  noCelebrantOptions.forEach((noCelebrantOption) => {
    const noCelebrantOptionFound = allCelebrantOptions.find(
      ({ label }) => label === noCelebrantOption
    )

    if (noCelebrantOptionFound)
      sortedNoCelebrantOptions.push(noCelebrantOptionFound)
  })

  const celebrantOptions = allCelebrantOptions.filter(
    (celebrantOption) => !noCelebrantOptions.includes(celebrantOption.label)
  )

  const sortedCelebrantOptions = sortBy(celebrantOptions, ({ label }) => label)

  return [...sortedNoCelebrantOptions, ...sortedCelebrantOptions]
}

export interface CountryOption {
  label: string
  value: number
}

export const formatCountryOptions = (
  countries: Country[] = []
): CountryOption[] => {
  const countryOptions = countries.map((country) => ({
    label: country.attributes.name,
    value: country.id,
  }))

  return sortBy(countryOptions, ({ label }) => label)
}

export const formatProductName = (key: string): string => {
  switch (key) {
    case 'lpa':
      return upperCase(key)

    case 'will':
      return 'Telephone wills'

    default:
      return formatToHuman(key)
  }
}

/** I am giving this type a generic name as ideally it will be used to type other options in future besides trees */
export interface StringValueFormOption {
  label: string
  value: string
}

export const formatPackageOptions = ({
  brandId,
  isPlanRedemption,
  packages,
  currentPackageSlug,
}: {
  brandId: FuneralBrandId
  isPlanRedemption: boolean
  packages: Constants['attributes']['funeralPackagePrices']
  /** Included even if not availableForNewLeads, so a package that's already
   * selected (and being shown read-only rather than freely re-editable)
   * doesn't disappear from its own dropdown. */
  currentPackageSlug?: string
}): StringValueFormOption[] => {
  if (!brandId || !packages) return []

  const brandPackages = packages[brandId]
  const packageSlugs = Object.keys(brandPackages).filter(
    (slug) =>
      brandPackages[slug].availableForNewLeads || slug === currentPackageSlug
  )
  return packageSlugs.map((slug) => ({
    label: `${formatToHuman(brandPackages[slug].displayName)} ${
      !isPlanRedemption ? `(+£${brandPackages[slug].price})` : ''
    }`.trim(),
    value: slug,
  }))
}

/** This method checks if this is a funeral plan redemption
 * and if so, returns 0 as the price to be displayed for ashes delivery.
 * Otherwise, it returns the passed `price` value without modification. */
const getAshesDeliveryDisplayPrice = ({
  isPlanRedemption,
  price,
}: {
  isPlanRedemption: boolean
  price: number | undefined
}): number | undefined => {
  return isPlanRedemption ? 0 : price
}

export const formatAshesInstructionsOptions = ({
  brandId,
  packageSlug,
  isPlanRedemption,
  prices,
}: {
  brandId: FuneralBrandId
  packageSlug?: string
  isPlanRedemption: boolean
  prices: Constants['attributes']['ashesDeliveryPrices']
}): StringValueFormOption[] => {
  const ashesDeliveryPrice = getAshesDeliveryDisplayPrice({
    isPlanRedemption,
    price:
      prices &&
      packageSlug &&
      prices[brandId] &&
      prices[brandId][packageSlug] !== undefined
        ? prices[brandId][packageSlug]
        : undefined,
  })

  const deliverLabel =
    ashesDeliveryPrice !== undefined
      ? `Deliver (${ashesDeliveryPrice > 0 ? `+£${ashesDeliveryPrice}` : '£0'})`
      : 'Deliver'

  return [
    { value: DELIVER, label: deliverLabel },
    { value: SCATTER, label: 'Scatter at crematorium' },
    {
      value: COLLECT,
      label: 'Collect from crematorium',
    },
  ]
}

/** Removes the passed `textToRemove` from the `name`, then formats to human-readable */
export const removeTextAndFormat = ({
  name,
  textToRemove,
}: {
  name: string
  textToRemove: string
}): string => {
  return formatToHuman(name.replace(textToRemove, ''))
}

export const formatTreeOptions = (
  trees: Constants['attributes']['treePrices']
): StringValueFormOption[] => {
  if (!trees) return []
  const treeNames = Object.keys(trees)
  const treeOptions = treeNames.map((name) => ({
    label: `${removeTextAndFormat({ name, textToRemove: 'tree_' })} (+£${
      trees[name]
    })`,
    value: name,
  }))

  return treeOptions
}

/** Urns that cost nothing because they aren't really a choice of urn, so they
 * can be picked for any portion. */
const URNS_FREE_IN_ANY_POSITION = ['undecided', 'supplied_by_customer']

/** Builds urn select options for one position in the urn list. Celebration
 * urns are offered in every position. Position affects price: the first urn
 * (isFirstUrn) is priced at its celebrationFirstUrnPrice when a celebration
 * has been chosen, and every urn at every other position is always priced at
 * its basePrice. Only one free urn is allowed per funeral, so an urn that is
 * always free (e.g. simple_container) is only offered as the first urn. */
export const formatUrnOptions = ({
  brandId,
  packageSlug,
  urns,
  isFirstUrn = false,
  hasChosenCelebration = false,
}: {
  brandId: FuneralBrandId
  packageSlug: string
  urns: Constants['attributes']['urnPrices']
  isFirstUrn?: boolean
  hasChosenCelebration?: boolean
}): StringValueFormOption[] => {
  if (!brandId || !packageSlug || !urns) return []

  /** Not every package has urn prices (e.g. Farewill plan packages), so
   * treat a missing entry as having no urns rather than crashing. */
  const packageUrns = urns[brandId]?.[packageSlug]
  if (!packageUrns) return []
  const urnNames = Object.keys(packageUrns).filter(
    (name) =>
      isFirstUrn ||
      packageUrns[name].basePrice !== 0 ||
      URNS_FREE_IN_ANY_POSITION.includes(name)
  )

  return urnNames.map((name) => {
    const { basePrice, celebrationFirstUrnPrice } = packageUrns[name]
    const price =
      isFirstUrn && hasChosenCelebration ? celebrationFirstUrnPrice : basePrice
    const displayName = name.startsWith('celebration_')
      ? removeTextAndFormat({ name, textToRemove: 'celebration_' })
      : formatToHuman(name)

    return {
      label: `${displayName} urn (+£${price})`,
      value: name,
    }
  })
}

export const formatCelebrationOptions = ({
  packageSlug,
  celebrations,
  includeNoneOption = false,
  hasPurchasedCelebration = false,
}: {
  packageSlug: string
  celebrations: Constants['attributes']['celebrationPrices']
  includeNoneOption?: boolean
  hasPurchasedCelebration?: boolean
}): StringValueFormOption[] => {
  if (!packageSlug || !celebrations || !celebrations[packageSlug]) return []

  const celebrationNames = Object.keys(celebrations[packageSlug])

  const celebrationOptions = celebrationNames.map((name) => ({
    label: `${removeTextAndFormat({ name, textToRemove: 'celebration_' })} (+£${
      hasPurchasedCelebration ? 0 : celebrations[packageSlug][name]
    })`,
    value: name,
  }))

  /** A celebration prepaid with the funeral plan can't be walked back to
   * None on the funeral/funeral lead pages. */
  if (includeNoneOption && !hasPurchasedCelebration) {
    celebrationOptions.push({ label: 'None', value: CELEBRATION_NONE })
  }

  return celebrationOptions
}

export const formatWillSuiteStatus = (status: string): string => {
  return status.replaceAll('_', ' ')
}

export const squareUrlFromTransactionId = (id: string): string => {
  return `${ENV.SQUARE_DASHBOARD_URL}/sales/transactions/${id}`
}

export const squareUrlForTransactionPage = (id: string): string => {
  return `${ENV.SQUARE_DASHBOARD_URL}/sales/transactions/${id}`
}

export const squareUrlForInvoicePage = (id: string): string => {
  return `${ENV.SQUARE_DASHBOARD_URL}/invoices/${id}`
}

export const stripeUrlForCustomerPage = (id: string): string => {
  return `${ENV.STRIPE_DASHBOARD_URL}/customers/${id}`
}

export const stripeUrlForInvoicePage = (id: string): string => {
  return `${ENV.STRIPE_DASHBOARD_URL}/invoices/${id}`
}

export const stripeUrlForTransactionPage = (id: string): string => {
  return `${ENV.STRIPE_DASHBOARD_URL}/payments/${id}`
}

export const getTransactionIdFromSquareUrl = (url: string): string | null => {
  return getIdFromExternalUrl(url, '/transactions/')
}

export const getCallIdFromAircallUrl = (url: string): string | null => {
  return getIdFromExternalUrl(url, '/calls/')
}

const getIdFromExternalUrl = (
  url: string,
  precedingCharacters: string
): string | null => {
  const regexPattern = `${precedingCharacters}([^/]+)`
  const matches = url.match(new RegExp(regexPattern))
  return matches ? matches[1] : null
}

export const aircallUrlFromCallId = (id: string): string => {
  return `https://assets.aircall.io/calls/${id}/recording`
}

export const convertPoundsToPence = (value: number | null): number | null => {
  if (value === null) return null
  const pounds = ensureNumberOrNull(value)
  return typeof pounds === 'number' ? Math.round(pounds * 100) : null
}

type ValueAndLabel = {
  value: string
  label: string
}

export const getLabel = (
  options: ValueAndLabel[],
  value: string | null
): string | undefined => {
  return options.find((option) => option.value === value)?.label
}

export const formatAddress = (
  address: Partial<Address>,
  separator = ', '
): string =>
  [
    address.lineOne,
    address.lineTwo,
    address.city,
    address.postalCode,
    address.countryCode,
  ]
    .filter(identity)
    .join(separator)

/**
 * In Formik, when there is no value, it will be presented as empty string,
 * this can cause problems while passing it to the backend API, as some of them
 * expect different type than string, e.g. number, boolean, etc.
 */
export const formatEmptyStringToUndefined = <T>(value: T) =>
  typeof value === 'string' && value === '' ? undefined : value

export const getFrontLink = (email?: string | null) => {
  const query = email
  return query
    ? `https://app.frontapp.com/inboxes/teams/4610822/inbox/unassigned/0/search/global/${encodeURI(
        query
      )}`
    : ''
}

export const getZendeskUser = (externalZendeskId: string) =>
  `https://farewill.zendesk.com/agent/users/${externalZendeskId}`

export const getYearsElapsed = ({
  dateOfBirth,
  dateOfDeath,
}: {
  dateOfBirth: string
  dateOfDeath?: string
}) => {
  // dates with no time, so worked out in UTC to keep them exact
  const endDate = dateOfDeath ? dateOfDeath : londonToday()
  return dayjsUtc(endDate).diff(dayjsUtc(dateOfBirth), 'year')
}

export const pluralise = (count: number, noun: string, suffix = 's') => {
  if (count > 1) {
    return `${noun}${suffix}`
  }
  return noun
}

export const isUrl = (url: string): boolean => {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export const isMarriedOrCivilPartnership = (maritalStatus?: string) => {
  if (!maritalStatus) return false
  return [MARRIED, CIVIL_PARTNERSHIP, MARRIED_OR_CIVIL_PARTNERSHIP].includes(
    maritalStatus
  )
}

/** used in NewContactForm and NewLead to retrieve what product options to display, based on the passed brandId  */
export const getAvailableProductsForBrand = (brandId: BrandId) => {
  if (!brandId) {
    return []
  }

  const products = ENV.FF_LPAS_ENABLED
    ? PRODUCTS_FOR_BRAND[brandId]
    : PRODUCTS_FOR_BRAND[brandId].filter((product) => product !== PRODUCTS.LPA)

  const options = products.map((product) => {
    return {
      value: product,
      label:
        FAREWILL_PRODUCTS_OPTIONS.find((option) => option.value === product)
          ?.label ?? product,
    }
  })

  return options
}
